import BaseService from './BaseService'

/**
 * @returns {string} Random idempotency token for POST /cart/batch.
 */
export function newCartBatchIdempotencyKey () {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

/**
 * Typed builders for `POST /cart/batch` ops (POS should send one batch per flush via the cart write coordinator).
 */
export const cartBatchOp = {
  addItem (payload) {
    return { op: 'add_item', payload }
  },
  setQuantity (lineItemId, quantity, metadata = undefined, price = undefined) {
    const payload = { line_item_id: Number(lineItemId), quantity: Number(quantity) }
    if (metadata !== undefined && metadata !== null) {
      payload.metadata = metadata
    }
    if (price !== undefined && price !== null) {
      payload.price = Number(price)
    }
    return { op: 'set_quantity', payload }
  },
  removeItem (lineItemId) {
    return { op: 'remove_item', payload: { line_item_id: Number(lineItemId) } }
  },
  setCustomer (customerId) {
    return { op: 'set_customer', payload: { customer_id: customerId == null ? null : customerId } }
  },
  setOrderDiscountFromSaleUi (typeUi, amount) {
    const amt = Number(amount) || 0
    if (!typeUi || amt <= 0) {
      return { op: 'set_order_discount', payload: { discount_type: null, discount_amount: 0 } }
    }
    return {
      op: 'set_order_discount',
      payload: {
        discount_type: typeUi === '%' ? 'percentage' : 'fixed',
        discount_amount: amt
      }
    }
  },
  clearCart () {
    return { op: 'clear_cart' }
  }
}

class CartService extends BaseService {
  constructor () {
    super('/cart')
  }

  /**
   * Get the authenticated user's cart
   * @returns {Promise<Object>} Cart data
   */
  async getCart () {
    try {
      return await this.customRequest('get', '')
    } catch (error) {
      console.error('Error fetching cart:', error)
      throw error
    }
  }

  /**
   * @deprecated Prefer POST /cart/batch from the POS cart write coordinator for register line mutations.
   * @param {Object} product - Product data
   * @param {number} quantity - Quantity to add
   * @param {Object} options - Additional options (discount, tax, etc.)
   * @returns {Promise<Object>} Updated cart data
   */
  async addItem (product, quantity = 1, options = {}) {
    try {
      const cartItem = {
        sku: product.barcode || String(product.id),
        product_id: product.id ?? null,
        name: product.name,
        price: product.price,
        quantity: quantity,
        metadata: {
          ...options
        }
      }

      return await this.customRequest('post', `/items`, cartItem)
    } catch (error) {
      console.error('Error adding item to cart:', error)
      throw error
    }
  }

  /**
   * @deprecated Prefer POST /cart/batch from the POS cart write coordinator for register line mutations.
   */
  async setQuantity (lineItemId, quantity, metadata = undefined, price = undefined) {
    try {
      const body = { quantity }
      if (metadata !== undefined && metadata !== null) {
        body.metadata = metadata
      }
      if (price !== undefined && price !== null) {
        body.price = Number(price)
      }
      return await this.customRequest('patch', `/items/${lineItemId}`, body)
    } catch (error) {
      console.error('Error updating cart item quantity:', error)
      throw error
    }
  }

  /**
   * @deprecated Prefer POST /cart/batch from the POS cart write coordinator for register line mutations.
   */
  async removeItem (lineItemId) {
    try {
      return await this.customRequest('delete', `/items/${lineItemId}`)
    } catch (error) {
      console.error('Error removing item from cart:', error)
      throw error
    }
  }

  /**
   * Clear the entire cart
   * @returns {Promise<Object>} Empty cart data
   */
  async clearCart() {
    try {
      return await this.customRequest('delete', '')
    } catch (error) {
      console.error('Error clearing cart:', error)
      throw error
    }
  }

  /**
   * Update multiple cart items at once (for bulk operations)
   * @param {Array} updates - Array of updates with sku and quantity
   * @returns {Promise<Object>} Updated cart data
   */
  async updateMultipleItems(updates) {
    try {
      const promises = updates.map(({ sku, quantity }) => this.setQuantity(sku, quantity))
      await Promise.all(promises)
      return await this.getCart()
    } catch (error) {
      console.error('Error updating multiple cart items:', error)
      throw error
    }
  }

  /**
   * Set or clear the customer associated with the cart.
   * @param {number|null} customerId
   * @returns {Promise<Object>} Updated cart data
   */
  async setCustomer(customerId) {
    try {
      return await this.customRequest('post', '/customer', {
        customer_id: customerId
      })
    } catch (error) {
      console.error('Error setting cart customer:', error)
      throw error
    }
  }

  /**
   * PATCH order-level discount stored with the cart (server-calculated summary on next GET).
   * @param {{ discount_type: string|null, discount_amount?: number }} body
   */
  async updateCartPricing (body) {
    return await this.customRequest('patch', '/pricing', body)
  }

  /**
   * Apply transactional batch (requires Idempotency-Key). Prefer `cartBatchOp` helpers to build `ops`.
   * @param {{ base_cart_revision?: number, ops: Array<{ op: string, payload?: object }> }} body
   * @param {string} idempotencyKey
   * @returns {Promise<Object>} Full cart payload with cart_revision, concurrent_cart_activity, op_results
   */
  async applyBatch (body, idempotencyKey) {
    try {
      return await this.customRequest('post', '/batch', body, {
        headers: { 'Idempotency-Key': idempotencyKey }
      })
    } catch (error) {
      console.error('Error applying cart batch:', error)
      throw error
    }
  }
}

export default new CartService()
